/**
 * A8 · EL MAPA DEL SISTEMA — la fuente única sobre qué pantallas existen.
 *
 * Vive en `shared-types` porque lo usan LOS DOS lados. Si el API tuviera su
 * propia copia, el copiloto ofrecería pantallas que el panel ya no tiene, o
 * filtros que dejaron de existir —y nadie se enteraría hasta que alguien tocara
 * un botón que no lleva a ninguna parte—.
 *
 * TRES COSAS QUE ESTE MAPA GARANTIZA (los cerrojos viven en el panel, que es
 * donde están las pantallas y los esquemas de filtro):
 *
 *  1. **Que no se olvide una pantalla.** `mapa-sistema.test.ts` lee
 *     `src/pages/` del disco y falla si aparece una carpeta que no esté acá ni
 *     excluida con motivo. No es documentación que se desactualiza en silencio:
 *     agregar una pantalla y no registrarla rompe el build.
 *
 *  2. **Que los filtros que declara existan.** El test cruza `filtros` contra
 *     `ESQUEMAS_POR_RUTA`. Si el mapa dijera que Trabajos filtra por "zona" y
 *     el esquema no lo tuviera, el copiloto armaría un enlace con un parámetro
 *     que se ignora en silencio.
 *
 *  3. **Que un botón nunca apunte a la nada.** `enlaceAPantalla` (en el panel)
 *     devuelve `null` para una ruta que no está acá, y descarta los filtros que
 *     la pantalla no acepta. El modelo no puede inventar destinos: solo elegir
 *     entre los que existen.
 *
 * El campo que hace posible todo eso es `responde`: las preguntas de negocio en
 * las palabras del dueño. Es lo que permite ir de *"¿cuántas instalaciones hay
 * mañana?"* a `/jobs?rango=manana`, sin que el modelo adivine por el nombre de
 * la pantalla.
 */

export interface PantallaDelSistema {
  /** Carpeta en `src/pages/`. Es la clave del cerrojo contra el disco. */
  carpeta: string;
  ruta: string;
  nombre: string;
  /** Qué hace, en el idioma del dueño. Nada de jerga de sistema. */
  queHace: string;
  quienLaVe: 'todos' | 'admin' | 'owner';
  /** Nombres de filtro, tal como viajan en la URL. Cruzados con el esquema. */
  filtros: string[];
  /** Qué se puede hacer desde acá, además de mirar. */
  acciones: string[];
  /** Preguntas de negocio que responde, en las palabras del dueño. */
  responde: string[];
}

/**
 * Carpetas de `src/pages/` que NO son pantallas del sistema.
 *
 * Cada una tiene que explicar por qué. Sin el motivo, esta lista se convierte
 * en el lugar donde se esconde lo que da pereza registrar.
 */
export const CARPETAS_SIN_PANTALLA: Record<string, string> = {
  login: 'Pantalla de entrada, antes de tener sesión. El copiloto nunca la ofrece.',
  auth: 'Aceptar invitación y recuperar contraseña: se llega por un enlace de correo, no por el menú.',
  tecnico:
    'Vista del teléfono del técnico. App.tsx la monta standalone, fuera del Layout y sin sidebar; no es parte del panel del dueño.',
  simulador:
    'Simulador del AMBIENTE DE PRUEBA: la ruta solo existe con el panel en modo pruebas y la API con AMBIENTE_PRUEBAS=1. El copiloto nunca debe ofrecerla en producción.',
};

export const MAPA_SISTEMA: PantallaDelSistema[] = [
  {
    carpeta: 'dashboard',
    ruta: '/',
    nombre: 'Resumen del día',
    queHace: 'Muestra de un vistazo cómo va el negocio hoy: leads calientes sin atender, trabajos del día, quejas y lo vendido.',
    quienLaVe: 'todos',
    filtros: [],
    acciones: ['Tocar un número para abrir su listado ya filtrado'],
    responde: ['¿Cómo vamos hoy?', '¿Hay algo urgente?', '¿Cuánto vendimos este mes?'],
  },
  {
    carpeta: 'asistente',
    ruta: '/asistente',
    nombre: 'Hablar con el asistente',
    queHace:
      'La conversación con el asistente interno, con el historial de todo lo que se habló y buscador. Es el mismo asistente que contesta por WhatsApp: lo que se habla acá se sigue allá.',
    quienLaVe: 'todos',
    filtros: [],
    acciones: [
      'Preguntar por el día, el equipo, los clientes o la plata',
      'Pedirle una auditoría completa',
      'Anotar un compromiso con un cliente',
      'Abrir una conversación nueva',
      'Buscar en lo que se habló antes',
    ],
    responde: [
      '¿Cómo va el día?',
      '¿Qué tengo que ver hoy?',
      '¿Quién está trabajando?',
      '¿Qué le prometimos a los clientes?',
      '¿De qué hablamos la semana pasada?',
    ],
  },
  {
    carpeta: 'pedidos',
    ruta: '/pedidos',
    nombre: 'Qué quieres que cambiemos',
    queHace:
      'El buzón de pedidos: contarle al asistente qué no funciona como esperabas o qué te gustaría que hiciera. Él va preguntando lo que hace falta para que se pueda construir, y queda anotado con un código.',
    quienLaVe: 'todos',
    filtros: [],
    acciones: [
      'Contar un problema hablando, sin llenar un formulario',
      'Ver los pedidos que ya se hicieron y cómo van',
      'Marcar un pedido como aceptado o en análisis (solo admin)',
    ],
    responde: [
      '¿Cómo pido un cambio?',
      '¿Qué pasó con lo que pedí?',
      'Esto no funciona como esperaba',
      'Me gustaría que el sistema hiciera otra cosa',
    ],
  },
  {
    carpeta: 'copiloto-historial',
    ruta: '/historial-copiloto',
    nombre: 'Historial del copiloto',
    // quienLaVe 'owner' es lo más cercano, pero no es exacto: además del dueño
    // de la cuenta la abre quien figure como DUEÑO en el directorio del
    // asistente, aunque en el panel sea admin. Por eso lo dice queHace.
    queHace:
      'Todo lo que cada persona del equipo habló con el asistente, sin borrar nada: quién escribió, cuándo y desde dónde (el botón flotante, la pantalla del asistente, WhatsApp, la puesta en marcha o un pedido de cambio). Solo la abren el dueño de la cuenta y quien figure como dueño en el directorio del asistente.',
    quienLaVe: 'owner',
    filtros: [],
    acciones: [
      'Filtrar por persona, por origen, por fechas o por palabras',
      'Leer una conversación completa, con la hora de cada mensaje',
    ],
    responde: [
      '¿Qué le preguntaron al asistente?',
      '¿Quién habló con el asistente ayer?',
      '¿Qué le contestó el asistente a alguien del equipo?',
      '¿Alguien le escribió al asistente por WhatsApp?',
    ],
  },
  {
    carpeta: 'conversations',
    ruta: '/conversations',
    nombre: 'Conversaciones',
    queHace: 'Los chats de WhatsApp con los clientes. Desde acá se responde, se asigna a alguien y se marca la temperatura del lead.',
    quienLaVe: 'todos',
    filtros: ['sinAtender', 'status', 'leadStatus', 'channelId', 'type', 'search'],
    acciones: ['Responder', 'Asignar a alguien', 'Cambiar la temperatura del lead'],
    responde: [
      '¿Qué clientes están esperando respuesta?',
      '¿Cuáles leads calientes no atendió nadie?',
      '¿Qué me escribió tal cliente?',
    ],
  },
  {
    carpeta: 'pendientes',
    ruta: '/pendientes',
    nombre: 'Pendientes',
    queHace: 'Los compromisos que quedaron con clientes y todavía no se cumplieron: llamar, mandar una cotización, responder algo que quedó en visto.',
    quienLaVe: 'admin',
    filtros: ['estado', 'vendedor', 'ventas'],
    acciones: ['Marcar cumplido', 'Reprogramar', 'Descartar con motivo'],
    responde: [
      '¿Qué le prometimos a los clientes y no cumplimos?',
      '¿Qué pendientes de venta están vencidos?',
      '¿Quién tiene compromisos sin atender?',
    ],
  },
  {
    carpeta: 'descartes',
    ruta: '/descartes',
    nombre: 'Descartados',
    queHace:
      'Los clientes a los que se les escribió dos veces y no contestaron. No están bloqueados: si vuelven a escribir salen solos de acá y recuperan sus dos intentos. Si la lista crece de golpe, puede que el filtro de escalamiento esté dejando pasar ventas.',
    quienLaVe: 'admin',
    filtros: [],
    acciones: ['Rescatar (lo saca del descarte y le devuelve sus intentos)'],
    responde: [
      '¿A quién le escribimos y nunca contestó?',
      '¿Cuántos chats terminaron descartados?',
      '¿Se descartó alguien que sí quería comprar?',
    ],
  },
  {
    carpeta: 'crm',
    ruta: '/crm',
    nombre: 'Embudo de ventas',
    queHace: 'El pipeline comercial: en qué etapa está cada lead y cuáles se están enfriando.',
    quienLaVe: 'todos',
    filtros: ['search', 'tagId', 'modelo', 'vista', 'sort'],
    acciones: ['Mover un lead de etapa', 'Etiquetar', 'Ver en tablero o en tabla'],
    responde: ['¿Cómo viene el embudo?', '¿Qué leads están por cerrar?', '¿Qué modelo se está pidiendo más?'],
  },
  {
    carpeta: 'clientes',
    ruta: '/clientes',
    nombre: 'Clientes',
    queHace:
      'La cartera del negocio: cada persona que escribió, qué buscaba, qué talla dijo y quién la está atendiendo. En un negocio con trabajos muestra además qué compró y cuánto ha dejado.',
    quienLaVe: 'admin',
    filtros: ['search', 'zona', 'compro', 'rango', 'desde', 'hasta'],
    acciones: ['Ver la ficha del cliente'],
    responde: ['¿Quiénes son mis clientes?', '¿Cuánto compró tal persona?', '¿Quién compró este mes?'],
  },
  {
    carpeta: 'contacts',
    ruta: '/contacts',
    nombre: 'Quién escribió',
    queHace: 'Todas las personas que escribieron a los canales, hayan comprado o no.',
    quienLaVe: 'todos',
    filtros: ['search'],
    acciones: ['Ver la ficha', 'Agregar una nota'],
    responde: ['¿Tengo el número de tal persona?', '¿Quién nos escribió?'],
  },
  {
    carpeta: 'jobs',
    ruta: '/jobs',
    nombre: 'Trabajos',
    queHace: 'Las instalaciones y garantías: qué hay agendado, quién lo tiene y en qué estado va cada una.',
    quienLaVe: 'admin',
    filtros: ['estado', 'tecnico', 'buscar', 'orden', 'rango', 'desde', 'hasta', 'vencidos', 'fueraDeRadio', 'pruebas', 'campo', 'garantia'],
    acciones: ['Crear un trabajo', 'Asignar técnico', 'Validar el cierre', 'Abrir una garantía', 'Cancelar', 'Exportar a Excel'],
    responde: [
      '¿Cuántas instalaciones hay mañana?',
      '¿Qué trabajos están vencidos sin que el técnico salga?',
      '¿Qué garantías hay abiertas?',
      '¿Qué trabajos cerraron lejos del cliente?',
    ],
  },
  {
    carpeta: 'despachos',
    ruta: '/despachos',
    nombre: 'Despachos',
    queHace: 'La agenda de salidas: qué hay que despachar hoy y mañana, con su ficha.',
    quienLaVe: 'admin',
    filtros: ['rango', 'desde', 'hasta'],
    acciones: ['Completar la ficha de despacho', 'Asignar técnico'],
    responde: ['¿Qué sale hoy?', '¿Qué hay que preparar para mañana?'],
  },
  {
    carpeta: 'tracking',
    ruta: '/tracking',
    nombre: 'Técnicos en la calle',
    queHace: 'Dónde está cada técnico ahora mismo y qué tiene asignado.',
    quienLaVe: 'admin',
    filtros: ['zona'],
    acciones: ['Ver la ubicación en el mapa'],
    responde: ['¿Dónde está tal técnico?', '¿Quién está cerca de esta zona?'],
  },
  {
    carpeta: 'employees',
    ruta: '/employees',
    nombre: 'Empleados',
    queHace: 'El equipo de campo: técnicos, sus habilidades, su nivel y su teléfono.',
    quienLaVe: 'admin',
    filtros: [],
    acciones: ['Agregar un técnico', 'Editar habilidades y nivel', 'Activar o desactivar'],
    responde: ['¿Quiénes son mis técnicos?', '¿Quién sabe instalar en madera?'],
  },
  {
    carpeta: 'campaigns',
    ruta: '/campaigns',
    nombre: 'Campañas',
    queHace: 'Los envíos masivos a listas de contactos.',
    quienLaVe: 'todos',
    filtros: [],
    acciones: ['Crear una campaña', 'Ver los resultados'],
    responde: ['¿Qué campañas mandamos?', '¿Cómo le fue a la última campaña?'],
  },
  {
    carpeta: 'agents',
    ruta: '/agents',
    nombre: 'Agentes AI',
    queHace: 'Los asistentes que responden por WhatsApp: su personalidad, qué saben y cuándo entran.',
    quienLaVe: 'todos',
    filtros: [],
    acciones: ['Editar el prompt', 'Activar o desactivar un agente'],
    responde: ['¿Qué le dice el asistente a los clientes?', '¿Está prendido el asistente?'],
  },
  {
    carpeta: 'entrenamiento',
    ruta: '/entrenamiento',
    nombre: 'Entrenamiento',
    queHace: 'Corregir al asistente: se toma una conversación real y se le enseña qué debió contestar.',
    quienLaVe: 'admin',
    filtros: [],
    acciones: ['Marcar una respuesta como ejemplo', 'Entrenar sobre una conversación'],
    responde: ['¿Cómo le enseño al asistente?', '¿Por qué contestó eso?'],
  },
  {
    carpeta: 'channels',
    ruta: '/channels',
    nombre: 'Canales',
    queHace: 'Los números de WhatsApp conectados y su estado.',
    quienLaVe: 'todos',
    filtros: [],
    acciones: ['Conectar un número', 'Escanear el QR', 'Desconectar'],
    responde: ['¿Está conectado el WhatsApp?', '¿Por qué no salen los mensajes?'],
  },
  {
    carpeta: 'installation',
    ruta: '/installation',
    nombre: 'Mi negocio',
    queHace: 'La configuración del negocio: catálogo de modelos, zonas, textos de los mensajes y a quién le llega cada aviso.',
    quienLaVe: 'admin',
    filtros: [],
    acciones: ['Editar catálogos', 'Cambiar los textos de los avisos', 'Elegir a quién le llega cada aviso'],
    responde: ['¿Dónde cambio los precios?', '¿Cómo cambio el mensaje que reciben los clientes?', '¿A quién le llegan los avisos?'],
  },
  {
    carpeta: 'tareas',
    ruta: '/tareas',
    nombre: 'Lo que falta configurar',
    queHace:
      'La lista de lo que el sistema necesita saber del negocio para trabajar completo (meses de garantía, quién recibe los avisos, cómo se ubica al cliente en el mapa). Cada punto se marca solo cuando el dato queda cargado. Tiene también notas propias para ir tachando.',
    quienLaVe: 'admin',
    filtros: [],
    acciones: [
      'Poner los meses de garantía de los modelos',
      'Abrir la guía que lleva al campo que falta',
      'Anotar y tachar notas propias',
      'Ocultar la tarjeta del resumen',
    ],
    responde: [
      '¿Qué me falta configurar?',
      '¿Qué tengo pendiente de completar en el sistema?',
      '¿Dónde pongo los meses de garantía?',
    ],
  },
  {
    carpeta: 'zonas',
    ruta: '/zonas',
    nombre: 'Ordenar las zonas',
    queHace:
      'Junta la misma zona escrita de varias formas en los trabajos viejos, para que los reportes la cuenten como una sola. Nada cambia hasta que el dueño aprueba.',
    quienLaVe: 'admin',
    filtros: [],
    acciones: ['Elegir a qué zona va cada grupo', 'Aprobar la agrupación'],
    responde: ['¿Por qué la misma zona sale repetida en los reportes?', '¿Cómo junto zonas mal escritas?'],
  },
  {
    carpeta: 'settings',
    ruta: '/settings',
    nombre: 'Ajustes técnicos',
    queHace: 'Lo técnico: la clave de la IA y las notificaciones del sistema.',
    quienLaVe: 'admin',
    filtros: [],
    acciones: ['Cargar la clave de IA', 'Configurar notificaciones'],
    responde: ['¿Dónde pongo la clave de la IA?'],
  },
  {
    carpeta: 'users',
    ruta: '/users',
    nombre: 'Usuarios',
    queHace: 'Las cuentas que entran al panel, con su rol. Son también el catálogo de vendedores.',
    quienLaVe: 'admin',
    filtros: ['buscar', 'rol', 'estado'],
    acciones: [
      'Buscar a alguien por nombre, email o teléfono',
      'Invitar a alguien',
      'Cambiar el rol',
      'Restablecer la clave',
      'Desactivar una cuenta',
    ],
    responde: [
      '¿Quién tiene acceso al sistema?',
      '¿Cómo le doy acceso a alguien?',
      '¿Quién está desactivado?',
      '¿Quiénes son los técnicos con cuenta?',
    ],
  },
  {
    carpeta: 'sistema',
    ruta: '/sistema',
    nombre: 'Estado del sistema',
    queHace: 'Si todo está funcionando: los canales, los procesos de fondo y los errores recientes.',
    quienLaVe: 'admin',
    filtros: [],
    acciones: ['Ver los errores recientes'],
    responde: ['¿Está todo funcionando?', '¿Por qué falló algo?'],
  },
  {
    carpeta: 'consumos',
    ruta: '/consumos',
    nombre: 'Gasto de IA',
    queHace: 'Cuánto se lleva gastado en inteligencia artificial este mes, con el detalle por modelo.',
    quienLaVe: 'owner',
    filtros: [],
    acciones: ['Ver el detalle por modelo'],
    responde: ['¿Cuánto llevo gastado en IA?', '¿Qué me está costando más?'],
  },
  {
    carpeta: 'limites',
    ruta: '/limites',
    nombre: 'Topes de gasto',
    queHace: 'El tope diario de gasto en inteligencia artificial: cuando se alcanza, el asistente deja de responder.',
    quienLaVe: 'owner',
    filtros: [],
    acciones: ['Cambiar el tope diario'],
    responde: ['¿Cuánto puedo gastar por día?', '¿Por qué se apagó el asistente?'],
  },
  {
    carpeta: 'desempeno',
    ruta: '/desempeno',
    nombre: 'Desempeño de técnicos',
    queHace:
      'Cuántas garantías está dejando cada técnico por cada 10 instalaciones que hace, en el rango de fechas que se elija.',
    quienLaVe: 'admin',
    filtros: ['rango', 'desde', 'hasta', 'orden'],
    acciones: [
      'Cambiar el rango de fechas',
      'Ordenar por la columna que se quiera',
    ],
    responde: [
      '¿Qué técnico me está dejando más garantías?',
      '¿Quién trabaja mejor?',
      '¿A quién le quedan fichas sin llenar?',
    ],
  },
];
